// ============================================
// FILE: /frontend/src/pages/admin/SocialMediaCampaignsPage.jsx
// PURPOSE: Admin marketing campaigns with dynamic filtering
// IMPORTS: Existing WhatsApp/Facebook generators
// ============================================

import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import { listingsAPI } from '../../api/listings.api';
import { makes } from '../../lib/vehicles';
import { propertyTypes } from '../../lib/categories';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Loader2, Filter, CheckSquare, Square, AlertCircle } from 'lucide-react';
import BulkMessageGenerator from '../../components/whatsapp/BulkMessageGenerator';
import BulkCollageGenerator from '../../components/facebook/BulkCollageGenerator';
import QuickFiltersBar from '../../components/listings/QuickFiltersBar';

export default function AdminMarketingDashboard() {
  console.log('🎨 [AdminMarketingDashboard] Component mounted');

  const { user } = useAuth();
  const { toast } = useToast();

  // ========================================
  // STATE - CAMPAIGN BUILDER
  // ========================================
  const [campaignName, setCampaignName] = useState('');
  const [category, setCategory] = useState('');
  const [selectedMake, setSelectedMake] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedSubType, setSelectedSubType] = useState('');

  // ========================================
  // STATE - LISTINGS
  // ========================================
  const [allListings, setAllListings] = useState([]);
  const [filteredListings, setFilteredListings] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtering, setFiltering] = useState(false);
  const [quickFilterConfig, setQuickFilterConfig] = useState(null);

  // ========================================
  // FETCH ALL APPROVED LISTINGS ON MOUNT
  // ========================================
  useEffect(() => {
    fetchAllListings();
  }, []);

  const fetchAllListings = async () => {
  console.log('🔵 [AdminMarketingDashboard] Fetching all approved listings...');
  setLoading(true);

  try {
    let allFetchedListings = [];
    let currentPage = 1;
    let hasMore = true;

    // Fetch in batches of 100 (backend limit)
    while (hasMore) {
      const data = await listingsAPI.getListings({
        status: 'APPROVED',
        sortBy: 'createdAt',
        sortOrder: 'desc',
        limit: 100,
        page: currentPage
      });

      const listings = data.listings || [];
      allFetchedListings = [...allFetchedListings, ...listings];

      console.log(`✅ [AdminMarketingDashboard] Page ${currentPage}: ${listings.length} listings`);

      // Check if there are more pages
      if (listings.length < 100 || !data.pagination?.hasNext) {
        hasMore = false;
      } else {
        currentPage++;
      }
    }

    console.log('✅ [AdminMarketingDashboard] Total listings fetched:', allFetchedListings.length);
    setAllListings(allFetchedListings);
  } catch (error) {

      console.error('❌ [AdminMarketingDashboard] Fetch error:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Could not load listings. Please refresh.',
      });
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // DYNAMIC DROPDOWN DATA
  // ========================================
  const availableModels = selectedMake
    ? makes.find((m) => m.name === selectedMake)?.models || []
    : [];

  const availableSubTypes = selectedType
    ? propertyTypes.find((p) => p.name === selectedType)?.subTypes || []
    : [];

  // ========================================
  // HANDLE FILTER BUTTON CLICK
  // ========================================
  const handleFilter = () => {
    console.log('🔵 [AdminMarketingDashboard] Filtering listings...', {
      category,
      selectedMake,
      selectedModel,
      selectedType,
      selectedSubType,
    });

    setFiltering(true);

    setTimeout(() => {
      let results = allListings.filter((listing) => {
        // Must match category
        if (category === 'VEHICLE') {
          if (listing.category !== 'VEHICLE') return false;
          if (selectedMake && listing.make !== selectedMake) return false;
          if (selectedModel && listing.model !== selectedModel) return false;
          return true;
        } else if (category === 'PROPERTY') {
          if (listing.category !== 'PROPERTY') return false;
          if (selectedType && listing.type !== selectedType) return false;
          if (selectedSubType && listing.subType !== selectedSubType) return false;
          return true;
        }
        return false;
      });

      // Apply quick filters if active
      if (quickFilterConfig?.where) {
        results = results.filter((listing) => {
          const where = quickFilterConfig.where;
          
          // Price filters
          if (where.price?.lte && listing.price > where.price.lte) return false;
          
          // Featured filter
          if (where.isFeatured !== undefined && listing.isFeatured !== where.isFeatured) return false;
          
          // Group buy filter
          if (where.allowGroupBuy !== undefined && listing.allowGroupBuy !== where.allowGroupBuy) return false;
          
          // Premium sellers filter
          if (where.user?.subscriptionTier?.in) {
            const tier = listing.user?.subscriptionTier;
            if (!where.user.subscriptionTier.in.includes(tier)) return false;
          }
          
          // This week filter
          if (where.createdAt?.gte) {
            const listingDate = new Date(listing.createdAt);
            const filterDate = new Date(where.createdAt.gte);
            if (listingDate < filterDate) return false;
          }
          
          return true;
        });
      }

      // Apply orderBy if specified
      if (quickFilterConfig?.orderBy) {
        const orderKey = Object.keys(quickFilterConfig.orderBy)[0];
        const orderDir = quickFilterConfig.orderBy[orderKey];
        
        results.sort((a, b) => {
          const aVal = a[orderKey] || 0;
          const bVal = b[orderKey] || 0;
          return orderDir === 'desc' ? bVal - aVal : aVal - bVal;
        });
      }

      console.log('✅ [AdminMarketingDashboard] Filtered with quick filters:', results.length);

      setFilteredListings(results);
      setSelectedIds([]); // Clear selection
      setFiltering(false);

      toast({
        title: 'Filter Applied',
        description: `Found ${results.length} listings matching your criteria.`,
      });
    }, 300);
  };

  // ========================================
  // HANDLE RESET
  // ========================================
  const handleReset = () => {
    console.log('🔄 [AdminMarketingDashboard] Resetting filters...');
    setCampaignName('');
    setCategory('');
    setSelectedMake('');
    setSelectedModel('');
    setSelectedType('');
    setSelectedSubType('');
    setQuickFilterConfig(null);
    setFilteredListings([]);
    setSelectedIds([]);
  };

  // ========================================
  // HANDLE LISTING SELECTION
  // ========================================
  const toggleSelection = (listingId) => {
    setSelectedIds((prev) => {
      if (prev.includes(listingId)) {
        return prev.filter((id) => id !== listingId);
      } else {
        if (prev.length >= 6) {
          toast({
            variant: 'destructive',
            title: 'Maximum reached',
            description: 'You can select up to 6 listings',
          });
          return prev;
        }
        return [...prev, listingId];
      }
    });
  };

  const selectAll = () => {
    const allIds = filteredListings.slice(0, 6).map((l) => l.id);
    setSelectedIds(allIds);
  };

  const deselectAll = () => {
    setSelectedIds([]);
  };

  // ========================================
  // GET SELECTED LISTINGS FOR GENERATORS
  // ========================================
  const selectedListings = filteredListings.filter((l) =>
    selectedIds.includes(l.id)
  );

  // ========================================
  // RENDER
  // ========================================
  console.log('🎨 [AdminMarketingDashboard] Rendering with state:', {
    category,
    allListingsCount: allListings.length,
    filteredCount: filteredListings.length,
    selectedCount: selectedIds.length,
  });

  if (user?.role !== 'ADMIN') {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Access denied. This page is only available to administrators.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold">Marketing Dashboard</h1>
        <p className="text-muted-foreground mt-2">
          Create themed campaigns by filtering listings and sharing to WhatsApp/Facebook groups
        </p>
      </div>

      {/* Campaign Builder */}
      <Card>
        <CardHeader>
          <CardTitle>Campaign Builder</CardTitle>
          <CardDescription>
            Select filters to find listings, then name your campaign (e.g., "Probox Monday")
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Campaign Name */}
          <div className="space-y-2">
            <Label htmlFor="campaignName">Campaign Name</Label>
            <Input
              id="campaignName"
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
              placeholder='e.g., "Probox Monday", "Warehouse Wednesday"'
            />
          </div>

          {/* Category Selection */}
          <div className="space-y-2">
            <Label htmlFor="category">Category</Label>
            <Select
              onValueChange={(value) => {
                setCategory(value);
                setSelectedMake('');
                setSelectedModel('');
                setSelectedType('');
                setSelectedSubType('');
                setFilteredListings([]);
                setSelectedIds([]);
              }}
              value={category}
            >
              <SelectTrigger id="category">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="VEHICLE">Vehicles</SelectItem>
                <SelectItem value="PROPERTY">Properties</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* ADD THIS BLOCK - Quick Filters Bar */}
          {category && (
            <div className="space-y-2">
              <Label>Quick Filters (Optional)</Label>
              <QuickFiltersBar
                category={category}
                onFilterApply={setQuickFilterConfig}
              />
            </div>
          )}

          {/* VEHICLE FILTERS */}
          {category === 'VEHICLE' && (
            <div className="grid md:grid-cols-2 gap-6">
              {/* Make */}
              <div className="space-y-2">
                <Label htmlFor="make">Make</Label>
                <Select
                  onValueChange={(value) => {
                    setSelectedMake(value);
                    setSelectedModel('');
                  }}
                  value={selectedMake}
                >
                  <SelectTrigger id="make">
                    <SelectValue placeholder="Select make" />
                  </SelectTrigger>
                  <SelectContent>
                    {makes.map((make) => (
                      <SelectItem key={make.name} value={make.name}>
                        {make.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Model */}
              <div className="space-y-2">
                <Label htmlFor="model">Model (Optional)</Label>
                <Select
                  onValueChange={setSelectedModel}
                  value={selectedModel}
                  disabled={!selectedMake}
                >
                  <SelectTrigger id="model">
                    <SelectValue placeholder="Select model" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableModels.map((model) => (
                      <SelectItem key={model} value={model}>
                        {model}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* PROPERTY FILTERS */}
          {category === 'PROPERTY' && (
            <div className="grid md:grid-cols-2 gap-6">
              {/* Type */}
              <div className="space-y-2">
                <Label htmlFor="type">Type</Label>
                <Select
                  onValueChange={(value) => {
                    setSelectedType(value);
                    setSelectedSubType('');
                  }}
                  value={selectedType}
                >
                  <SelectTrigger id="type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {propertyTypes.map((type) => (
                      <SelectItem key={type.name} value={type.name}>
                        {type.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* SubType */}
              {availableSubTypes.length > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="subType">Sub-Type (Optional)</Label>
                  <Select
                    onValueChange={setSelectedSubType}
                    value={selectedSubType}
                    disabled={!selectedType}
                  >
                    <SelectTrigger id="subType">
                      <SelectValue placeholder="Select sub-type" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableSubTypes.map((subType) => (
                        <SelectItem key={subType} value={subType}>
                          {subType}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              onClick={handleFilter}
              disabled={!category || filtering || loading}
              className="flex-1"
            >
              {filtering ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Filtering...
                </>
              ) : (
                <>
                  <Filter className="w-4 h-4 mr-2" />
                  Filter Listings
                </>
              )}
            </Button>
            <Button variant="outline" onClick={handleReset}>
              Reset
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Filtered Results */}
      {filteredListings.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>
                  {campaignName || 'Campaign Results'}{' '}
                  <Badge variant="secondary" className="ml-2">
                    {filteredListings.length} listings
                  </Badge>
                </CardTitle>
                <CardDescription>
                  Select up to 6 listings to include in your campaign
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={selectAll}>
                  <CheckSquare className="w-4 h-4 mr-2" />
                  Select First 6
                </Button>
                <Button variant="outline" size="sm" onClick={deselectAll}>
                  <Square className="w-4 h-4 mr-2" />
                  Clear
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {filteredListings.map((listing) => {
                const isSelected = selectedIds.includes(listing.id);
                const API_BASE_URL = import.meta.env.VITE_BASE_URL || 'http://localhost:5000';
                const getImageUrl = () => {
                  if (Array.isArray(listing.imageUrl) && listing.imageUrl.length > 0) {
                    const img = listing.imageUrl[0];
                    return img.startsWith('http') ? img : `${API_BASE_URL}${img}`;
                  }
                  return '/placeholder-car.jpg';
                };

                return (
                  <div
                    key={listing.id}
                    onClick={() => toggleSelection(listing.id)}
                    className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition ${
                      isSelected
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                        isSelected
                          ? 'bg-primary border-primary'
                          : 'border-muted-foreground'
                      }`}
                    >
                      {isSelected && <CheckSquare className="w-4 h-4 text-white" />}
                    </div>

                    <div className="flex gap-3 flex-grow">
                      <img
                        src={getImageUrl()}
                        alt={listing.title}
                        className="w-16 h-16 object-cover rounded"
                        onError={(e) => {
                          e.target.src = '/placeholder-car.jpg';
                        }}
                      />
                      <div>
                        <h3 className="font-medium mb-1">{listing.title}</h3>
                        <p className="text-sm font-bold text-primary mb-1">
                          KES {parseFloat(listing.price).toLocaleString()}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {listing.subCounty}, {listing.county}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Generators (only show if listings selected) */}
      {selectedListings.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Generate Campaign</CardTitle>
            <CardDescription>
              Choose WhatsApp or Facebook to create your collage and message
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="whatsapp">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="whatsapp">WhatsApp</TabsTrigger>
                <TabsTrigger value="facebook">Facebook</TabsTrigger>
              </TabsList>

              <TabsContent value="whatsapp" className="mt-6">
                <BulkMessageGenerator
                  listings={selectedListings}
                  onMessageGenerated={(data) => {
                    console.log('✅ WhatsApp message generated:', data);
                  }}
                />
              </TabsContent>

              <TabsContent value="facebook" className="mt-6">
                <BulkCollageGenerator
                  listings={selectedListings}
                  onMessageGenerated={(data) => {
                    console.log('✅ Facebook message generated:', data);
                  }}
                />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {loading && (
        <Card>
          <CardContent className="py-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading listings...</p>
          </CardContent>
        </Card>
      )}

      {!loading && filteredListings.length === 0 && category && (
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              No listings found matching your filters. Try different criteria.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}